import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { SpringF } from '../../engine/math/spring';
import { easeOutBack } from '../../engine/math/easing';
import type { MeshData, Color } from '../../engine/geo/mesh';
import type { Ctx, Renderable } from '../Ctx';
import type { Player } from '../player/Player';
import type { Particles } from '../fx/Particles';

export type BeastKind = 'eel' | 'scarab' | 'stag' | 'wyrm';
export type BeastState = 'idle' | 'alert' | 'telegraph' | 'attack' | 'recover' | 'stagger' | 'dead';

export interface BeastPart { t: Transform; rd: Renderable; radius: number }

export const BEAST = { aggro: 18, lose: 30, loseTime: 8 };

/**
 * Common beast behaviour (§5): aggro/leash, one telegraphed attack (subclass), hit reaction,
 * cracks at half health (+15% speed), inflate-and-burst death. Beasts stay dead for the run.
 */
export abstract class Beast {
  readonly root: Transform;
  readonly parts: BeastPart[] = [];
  readonly cracks: Renderable[] = [];
  hp: number;
  state: BeastState = 'idle';
  alive = true;
  stateT = 0;
  speedMul = 1;
  readonly flash = SpringF.fromFreq(0, 9, 1);
  protected lastSeen = -100;
  protected aggro = false;
  readonly color: Color;
  private hurtCooldown = 0;
  readonly leashCenter: Vec3;

  constructor(protected readonly ctx: Ctx, readonly kind: BeastKind, pos: Vec3, readonly maxHp: number, color: Color, readonly leashRadius: number) {
    this.hp = maxHp;
    this.color = color;
    this.root = new Transform();
    this.root.position.copy(pos);
    this.root.snapPrev();
    this.leashCenter = pos.clone();
    ctx.spawn('beast.' + kind, this.root);
  }

  get position(): Vec3 { return this.root.position; }

  protected addPart(name: string, mesh: MeshData, parent: Transform, pos: Vec3, radius: number): BeastPart {
    const t = new Transform().setParent(parent);
    t.position.copy(pos);
    t.entity = this.ctx.spawn(name, t, mesh);
    const rd = this.ctx.renderables.req(t.entity);
    const part = { t, rd, radius };
    this.parts.push(part);
    return part;
  }
  /** A crack overlay that becomes visible at half health. */
  protected addCrack(name: string, mesh: MeshData, parent: Transform): Renderable {
    const t = new Transform().setParent(parent);
    t.entity = this.ctx.spawn(name, t, mesh, { visible: false, castShadow: false });
    const rd = this.ctx.renderables.req(t.entity);
    this.cracks.push(rd);
    return rd;
  }

  protected setState(s: BeastState): void { this.state = s; this.stateT = 0; }

  /** Aggro bookkeeping shared by all beasts. Returns true when the beast should engage. */
  protected updateAggro(player: Player, dt: number): boolean {
    const d = this.position.distanceTo(player.position);
    const t = this.ctx.time;
    if (player.state === 'dead') { this.aggro = false; return false; }
    if (d < BEAST.aggro && this.canSee(player)) { this.lastSeen = t; if (!this.aggro) this.onAggro?.(); this.aggro = true; }
    if (this.aggro && (d > BEAST.lose || t - this.lastSeen > BEAST.loseTime)) this.aggro = false;
    if (this.aggro && this.position.distanceTo(this.leashCenter) > this.leashRadius && d > 6) this.aggro = false;
    void dt;
    return this.aggro;
  }
  onAggro?: () => void;

  protected canSee(player: Player): boolean {
    const from = _a.copy(this.position); from.y += 1;
    const to = _b.copy(player.position); to.y += 1;
    const dir = _c.copy(to).sub(from);
    const d = dir.length();
    if (d < 0.5) return true;
    dir.scale(1 / d);
    const hit = this.ctx.statics.raycast(from, dir, d);
    return hit < 0;
  }

  /** Does a sword swing sphere touch this beast? Returns the part hit or null. */
  hitTest(center: Vec3, r: number): BeastPart | null {
    if (!this.alive) return null;
    for (const p of this.parts) {
      if (!p.rd.visible) continue;
      const wp = p.t.getWorldPosition(_a);
      if (wp.distanceTo(center) < r + p.radius) return p;
    }
    return null;
  }

  /** Take a hit. Returns false if the beast is dead or was hit too recently. */
  hurt(from: Vec3, particles: Particles, dmg = 1): boolean {
    if (!this.alive || this.hurtCooldown > 0) return false;
    this.hurtCooldown = 0.25;
    this.hp -= dmg;
    this.flash.value = 1;
    const dir = _a.copy(this.position).sub(from); dir.y = 0; dir.normalize();
    this.onKnockback(dir);
    particles.burst(_b.copy(this.position).add(_up), 10, this.color, 5, 0.16, 0.7, 0.5);
    this.ctx.fx.hitStop(0.06);
    this.ctx.sfx.hit(this.position);
    if (this.hp <= Math.ceil(this.maxHp / 2) && this.hp > 0) {
      for (const c of this.cracks) c.visible = true;
      this.speedMul = 1.15;
    }
    if (this.hp <= 0) this.die(particles);
    return true;
  }

  protected abstract onKnockback(dir: Vec3): void;

  die(particles: Particles): void {
    if (!this.alive) return;
    this.alive = false;
    this.setState('dead');
    this.ctx.sfx.kill(this.position);
    const parts = this.parts;
    const base = parts.map((p) => p.t.scale.clone());
    this.ctx.tweens.add(0.28, (k) => {
      const s = 1 + 0.3 * easeOutBack(k);
      parts.forEach((p, i) => p.t.scale.copy(base[i]).scale(s));
    }, undefined, () => {
      for (const p of parts) {
        const wp = p.t.getWorldPosition(_a);
        particles.burst(wp, 6, this.color, 4.5, 0.2 + p.radius * 0.2, 1.1, 0.35);
        p.rd.visible = false;
      }
      for (const c of this.cracks) c.visible = false;
      this.onDeath?.();
    });
  }
  onDeath?: () => void;

  /** Common per-step bookkeeping; subclasses call this first. */
  protected tick(dt: number): void {
    this.stateT += dt;
    this.hurtCooldown = Math.max(0, this.hurtCooldown - dt);
    this.flash.target = 0;
    const f = this.flash.update(dt);
    for (const p of this.parts) p.rd.flash = f;
  }

  /** Try to bite the player: sphere test against the player's capsule. */
  protected tryHitPlayer(player: Player, center: Vec3, r: number, knockback = 2): boolean {
    const pp = player.position;
    const dx = pp.x - center.x, dz = pp.z - center.z;
    const dy = Math.max(0, Math.max(pp.y - center.y, center.y - (pp.y + 1.7)));
    if (Math.hypot(dx, dz) < r + 0.32 && dy < r) return player.hurt(center, knockback);
    return false;
  }

  abstract update(dt: number, player: Player): void;

  serialize(): { kind: BeastKind; hp: number; alive: boolean; pos: [number, number, number] } {
    return { kind: this.kind, hp: this.hp, alive: this.alive, pos: [this.position.x, this.position.y, this.position.z] };
  }
  restore(s: { hp: number; alive: boolean; pos: [number, number, number] }): void {
    this.hp = s.hp;
    this.position.set(s.pos[0], s.pos[1], s.pos[2]);
    this.root.snapPrev();
    if (!s.alive) {
      this.alive = false; this.state = 'dead';
      for (const p of this.parts) p.rd.visible = false;
    } else if (this.hp <= Math.ceil(this.maxHp / 2)) { for (const c of this.cracks) c.visible = true; this.speedMul = 1.15; }
  }
}
const _a = new Vec3(), _b = new Vec3(), _c = new Vec3(), _up = new Vec3(0, 0.8, 0);
